import { useEffect, useRef, useState } from "react";
import { useTriviaContext } from "../context-trivia";

type TriviaTimerProps = {
  totalSeconds: number;
  onEnd: () => void;
};

export function TriviaTimer({ totalSeconds, onEnd }: TriviaTimerProps) {
  const { config } = useTriviaContext();
  const [remaining, setRemaining] = useState(totalSeconds);
  const [totalSecondsPrev, setTotalSecondsPrev] = useState(totalSeconds);
  const hasEndedRef = useRef(false);

  // A mounted timer can outlive its own duration (CMS live preview, where question ids are
  // index-based and so survive an edit to that question's time limit), meaning the countdown
  // has to resync when totalSeconds changes rather than only seeding via the useState
  // initializer. This is React's "adjust state when props change" pattern — resyncing from an
  // effect instead would trigger a cascading render.
  if (totalSecondsPrev !== totalSeconds) {
    setTotalSecondsPrev(totalSeconds);
    setRemaining(totalSeconds);
  }

  useEffect(
    function effectOnTimerTick() {
      // Re-arms the end guard for the new cycle. This lives here rather than beside the
      // resync above because refs cannot be written during render.
      hasEndedRef.current = false;

      if (totalSeconds <= 0) {
        return;
      }

      const interval = setInterval(() => {
        setRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }

          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    },
    [totalSeconds],
  );

  useEffect(
    function effectOnTimerEnd() {
      if (remaining === 0 && !hasEndedRef.current) {
        hasEndedRef.current = true;
        onEnd();
      }
    },
    [remaining, onEnd],
  );

  const percent = totalSeconds > 0 ? (remaining / totalSeconds) * 100 : 0;

  return (
    <div
      className="h-[max(0.35rem,1cqmin)] w-full overflow-hidden rounded-full"
      style={{ backgroundColor: config.theme.panelColor }}
    >
      <div
        className="h-full rounded-full transition-[width] duration-1000 ease-linear"
        style={{
          width: `${percent}%`,
          backgroundColor: config.theme.accentColor,
        }}
      />
    </div>
  );
}
